import React, { useState, useEffect } from 'react';
import { Card, Button, CreateCompanyWizard, CreateFinancialYearForm } from '../components';
import api from '../services/api';
import { useRole } from '../utils/RoleContext';
import '../styles/SelectContext.scss';
import '../styles/AddUser.scss';

const FolderIcon = () => (
  <svg viewBox="0 0 36 28" width="32" height="26" className="select-context-folder-icon">
    <path
      d="M2 5a2 2 0 0 1 2-2h8l3 3h17a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5z"
    />
  </svg>
);

const SelectContext = () => {
  const { role } = useRole();
  const isConsultant = role === 'Consultant';

  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [showCreateCompanyForm, setShowCreateCompanyForm] = useState(false);

  const [financialYears, setFinancialYears] = useState([]);
  const [fyLoading, setFyLoading] = useState(false);
  const [selectedFY, setSelectedFY] = useState('');
  const [showCreateFYForm, setShowCreateFYForm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    setCompaniesLoading(true);
    api.get('/core/companies/')
      .then(res => setCompanies(res.data))
      .catch(() => setError('Failed to load companies.'))
      .finally(() => setCompaniesLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedCompany) {
      setFinancialYears([]);
      setSelectedFY('');
      setShowCreateFYForm(false);
      return;
    }
    setSelectedFY('');
    setFyLoading(true);
    api.get(`/core/financial-years/?company_id=${selectedCompany}`)
      .then(res => setFinancialYears(res.data))
      .catch(() => setError('Failed to load financial years.'))
      .finally(() => setFyLoading(false));
  }, [selectedCompany]);

  const handleCompanyWizardDone = () => {
    window.location.href = '/';
  };

  const handleFYCreated = (fy) => {
    setFinancialYears(prev => [...prev, fy]);
    setSelectedFY(fy.id);
    setShowCreateFYForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/core/set-context/', {
        company_id: selectedCompany,
        financial_year_id: selectedFY,
      });
      localStorage.setItem('access_token', res.data.access);
      localStorage.setItem('refresh_token', res.data.refresh);
      localStorage.setItem('company_id', selectedCompany);
      localStorage.setItem('financial_year_id', selectedFY);
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set context. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const companiesEmpty = !companiesLoading && companies.length === 0;
  const fyEmpty = !fyLoading && financialYears.length === 0;
  const selectedCompanyName = companies.find(c => c.id === selectedCompany)?.name || '';
  const activeCompanies = companies.filter(c => !c.archived);
  const archivedCompanies = companies.filter(c => c.archived);
  const visibleCompanies = showArchived ? archivedCompanies : activeCompanies;

  return (
    <div className="select-context-container">
      <Card
        className={`select-context-card ${!selectedCompany ? 'select-context-card--wide' : ''}`}
      >
        {error && <div className="error-banner">{error}</div>}

        {!selectedCompany ? (
          <>
            {showArchived && (
              <button
                type="button"
                className="select-context-back-link"
                onClick={() => setShowArchived(false)}
              >
                &larr; Back to companies
              </button>
            )}

            <div className="select-context-header">
              <div>
                <h3 className="select-context-title">
                  {showArchived ? 'Archived' : 'Select a company'}
                </h3>
                <p className="select-context-subtitle">
                  {showArchived
                    ? 'Clients you are no longer working on.'
                    : 'Choose the company you want to work in.'}
                </p>
              </div>
              {isConsultant && !showArchived && (
                <button
                  type="button"
                  className="select-context-toggle-btn"
                  onClick={() => setShowCreateCompanyForm(v => !v)}
                >
                  {showCreateCompanyForm ? 'Cancel' : '+ Create new company'}
                </button>
              )}
            </div>

            {isConsultant && showCreateCompanyForm && !showArchived && (
              <div className="select-context-inline-form">
                <CreateCompanyWizard onDone={handleCompanyWizardDone} />
              </div>
            )}

            {companiesLoading ? (
              <p className="select-context-empty">Loading companies...</p>
            ) : companiesEmpty && !showCreateCompanyForm ? (
              <p className="select-context-empty">
                {isConsultant
                  ? 'You do not have any companies yet. Use "Create new company" above to get started.'
                  : 'No companies are associated with your account. Contact your consultant.'}
              </p>
            ) : (
              <div className="select-context-company-list">
                {visibleCompanies.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    className="select-context-company-row"
                    onClick={() => setSelectedCompany(c.id)}
                  >
                    <FolderIcon />
                    <span>{c.name}</span>
                  </button>
                ))}
                {isConsultant && !showArchived && archivedCompanies.length > 0 && (
                  <button
                    type="button"
                    className="select-context-company-row select-context-company-row--archived"
                    onClick={() => setShowArchived(true)}
                  >
                    <FolderIcon />
                    <span>Archived ({archivedCompanies.length})</span>
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="select-context-form">
            <button
              type="button"
              className="select-context-back-link"
              onClick={() => setSelectedCompany('')}
            >
              &larr; Back to companies
            </button>

            <div className="select-context-header">
              <div>
                <h3 className="select-context-title">{selectedCompanyName}</h3>
                <p className="select-context-subtitle">
                  Choose the financial year you want to work in.
                </p>
              </div>
              {isConsultant && !fyEmpty && (
                <button
                  type="button"
                  className="select-context-toggle-btn"
                  onClick={() => setShowCreateFYForm(v => !v)}
                >
                  {showCreateFYForm ? 'Cancel' : '+ Create new financial year'}
                </button>
              )}
            </div>

            <div className="form-group">
              {fyLoading ? (
                <p className="select-context-empty">Loading financial years...</p>
              ) : fyEmpty ? (
                <>
                  <p className="select-context-empty">
                    {isConsultant
                      ? 'This company has no financial years yet — create one to continue.'
                      : 'No financial years available for this company.'}
                  </p>
                  {isConsultant && (
                    <div className="select-context-inline-form">
                      <CreateFinancialYearForm companyId={selectedCompany} onCreated={handleFYCreated} />
                    </div>
                  )}
                </>
              ) : (
                <div className="select-context-company-list">
                  {financialYears.map(fy => (
                    <button
                      key={fy.id}
                      type="button"
                      className={`select-context-company-row ${selectedFY === fy.id ? 'select-context-company-row--selected' : ''}`}
                      onClick={() => setSelectedFY(fy.id)}
                    >
                      <FolderIcon />
                      <span>{fy.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {isConsultant && showCreateFYForm && !fyEmpty && (
              <div className="select-context-inline-form">
                <CreateFinancialYearForm companyId={selectedCompany} onCreated={handleFYCreated} />
              </div>
            )}

            <Button
              type="button"
              onClick={handleSubmit}
              fullWidth
              disabled={!selectedFY || loading}
            >
              {loading ? 'Setting context...' : 'Continue'}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SelectContext;
